import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AffiliateStockListing,
  CapiProductPage,
  Raffle
} from 'types/capi/pdp';

import HorizontalLine from '~/components/HorizontalLine';
import usePageData from '~/hooks/usePageData';
import useQsParams from '~/hooks/useQsParams';
import useRegion from '~/hooks/useRegion';
import useRetailerInteraction from '~/hooks/useRetailerInteraction';
import useUser from '~/hooks/useUser';
import gtm from '~/services/gtm.client';
import {
  getSortedStockListingsForSize,
  getStockStatusForSize
} from '~/utils/listings';

import Buttons from '../../components/Buttons';
import SizeSelection from '../../components/WhereToBuy/SizeSelection';
import GatedLoginCard from '../GatedLoginCard';
import RaffleListing from '../RaffleListing';
import StickyCta from './StickyCta';
import StockListing, { StockListingButtons } from './StockListing';
import StockListingGated from './StockListingGated';
import Title from './Title';
import ViewToggle from './ViewToggle';

export enum SelectedView {
  RAFFLES,
  RETAILERS
}

export default function WhereToBuyInStock() {
  const product = usePageData<CapiProductPage>();
  const { raffle, size } = useQsParams();
  const { t } = useTranslation('pdp');
  const region = useRegion();
  const user = useUser();
  const fireRetailerInteraction = useRetailerInteraction();

  const hasRaffles = product.stock_listings.raffles.length > 0;

  const [selectedView, setSelectedView] = useState(
    hasRaffles && raffle ? SelectedView.RAFFLES : SelectedView.RETAILERS
  );
  const [selectedSize, setSelectedSize] = useState(size);
  const [isExpanded, setIsExpanded] = useState(true);

  const stockListings = getSortedStockListingsForSize(product, selectedSize);
  const stockStatus = getStockStatusForSize(stockListings[0], selectedSize);

  const sizeSelectorRef = useRef<HTMLDivElement>(null);

  function onSizeSelectorClick() {
    sizeSelectorRef.current?.scrollIntoView({ behavior: 'smooth' });
    setIsExpanded(true);
    gtm.sendUserInteractionEvent('select size', 'Sticky CTA');
  }

  function trackBuyNowClick() {
    gtm.sendUserInteractionEvent('buy now', 'Sticky CTA');
  }

  const titleRef = useRef<HTMLDivElement>(null);

  const productPurchaseable =
    product.stock_status === 'in-stock' || product.stock_status === 'reseller';
  const shouldShowSizeGrid =
    selectedView !== SelectedView.RAFFLES &&
    product.allow_size_selection &&
    productPurchaseable;

  return (
    <>
      <div className="flex w-full flex-col gap-6 md:col-span-full lg:col-span-4">
        <div className="flex flex-col gap-5" ref={sizeSelectorRef}>
          <GatedLoginCard />
          <SizeSelection
            shouldShowSizeGrid={shouldShowSizeGrid}
            sizes={region.sizes}
            selectedSize={selectedSize}
            setSelectedSize={setSelectedSize}
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
          />
        </div>

        <div className="flex flex-col gap-5">
          <span ref={titleRef}>
            <Title text={t('whereToBuy')} />
          </span>
          <div className="flex w-full flex-col gap-4 rounded-lg bg-grey-panel p-4">
            {hasRaffles && (
              <ViewToggle
                selectedView={selectedView}
                setSelectedView={setSelectedView}
              />
            )}

            {selectedView === SelectedView.RETAILERS && (
              <StockListings
                selectedSize={selectedSize}
                listings={stockListings}
              />
            )}

            {selectedView === SelectedView.RAFFLES && (
              <Raffles raffles={product.stock_listings.raffles} />
            )}
          </div>
        </div>
      </div>
      {stockListings.length > 0 && selectedView === SelectedView.RETAILERS && (
        <StickyCta
          hideOnElementRef={titleRef}
          selectedSize={selectedSize}
          stockStatus={stockStatus}
          onSizeSelectorClick={onSizeSelectorClick}
        >
          {region.code !== 'GB' && !user.isLoggedIn ? (
            <Buttons.Gated
              pagePosition="Sticky CTA"
              showAfffiliateLogo
              dynamicVariant
            />
          ) : (
            <StockListingButtons
              stockStatus={stockStatus}
              selectedSize={selectedSize}
              listing={stockListings[0] as AffiliateStockListing}
              position={0}
              pagePosition="Sticky CTA"
              onBuyNowClick={trackBuyNowClick}
              onRetailerClick={() =>
                fireRetailerInteraction({
                  listing: stockListings[0] as AffiliateStockListing,
                  placement: 'sticky',
                  selectedSize
                })
              }
              showAfffiliateLogo
              dynamicVariant
            />
          )}
        </StickyCta>
      )}
    </>
  );
}

const StockListings = ({
  listings,
  selectedSize
}: {
  listings: AffiliateStockListing[];
  selectedSize?: string;
}) => {
  const region = useRegion();
  const user = useUser();

  return (
    <>
      {listings.map((listing, index) =>
        region.code !== 'GB' && !user.isLoggedIn ? (
          <StockListingGated
            key={listing.affiliate.name}
            position={index}
            pagePosition="WTB"
            listing={listing}
            selectedSize={selectedSize}
          />
        ) : (
          <StockListing
            key={listing.affiliate.name}
            position={index}
            pagePosition="WTB"
            listing={listing}
            selectedSize={selectedSize}
          />
        )
      )}
    </>
  );
};

const Raffles = ({ raffles }: { raffles: Raffle[] }) => {
  return (
    <>
      {raffles.map((raffle, index) => (
        <div key={raffle.id}>
          <RaffleListing raffleListing={raffle} buttonPosition={index} />
          <HorizontalLine />
        </div>
      ))}
    </>
  );
};
